import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { useEffect, useRef, useState } from "react";

interface InputYearProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  totalYears?: number;
}

const InputYear = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  className,
  totalYears = 100, // デフォルトで100年間を表示
}: InputYearProps) => {
  const { t } = useTranslation(defaultNS, { keyPrefix: "component.inputYear" });

  const currentYear = new Date().getFullYear();
  const middleYear = currentYear - 35; // リストの中央が現在の年から35年前

  // リストの開始年と終了年を計算
  const startYear = middleYear - Math.floor((totalYears - 1) / 2);
  const endYear = middleYear + Math.floor((totalYears - 1) / 2);

  // years 配列を生成
  const years: { value: string; label: string }[] = [];
  for (let year = startYear; year <= endYear; year++) {
    years.push({ value: year.toString(), label: `${year}${t("suffix")}` });
  }

  // 選択された項目または middleYear の項目を参照するための ref
  const selectedItemRef = useRef<HTMLDivElement | null>(null);

  // ドロップダウンの開閉状態を管理
  const [open, setOpen] = useState(false);

  // ドロップダウンが開かれたときに選択された項目または middleYear にスクロールする
  // open状態が変わった時のみ実行される
  useEffect(() => {
    if (open && selectedItemRef.current) {
      // タイムアウトを使用してUIの更新が完了した後にスクロールを実行
      const timeoutId = setTimeout(() => {
        if (selectedItemRef.current) {
          selectedItemRef.current.scrollIntoView({ block: "start" });
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [open]);

  return (
    <Select
      disabled={disabled}
      onValueChange={onChange}
      value={value}
      open={open}
      onOpenChange={setOpen}
    >
      <SelectTrigger className={className}>
        <SelectValue placeholder={placeholder || t("placeholder")} />
      </SelectTrigger>
      <SelectContent>
        {years.map(({ value: yearValue, label }) => (
          <SelectItem
            key={yearValue}
            value={yearValue}
            ref={
              yearValue === value ||
              (!value && yearValue === middleYear.toString())
                ? selectedItemRef
                : null
            }
          >
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default InputYear;
